import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { productImage } from '../lib/productImage';

const Industries = () => {
  const products = [
    {
      category: 'Acousstop Wooden Panels',
      tagline: 'NATURAL ELEGANCE',
      description: 'Grooved and perforated wood panels for walls and ceilings, with NRC ratings up to 0.95.',
      image: 'https://res.cloudinary.com/dwbfrbwul/image/upload/f_auto,q_auto/v1765090561/hillpoint/products/hillpoint/products/products-downloaded/niche-panels.jpg',
      link: '/products#wooden-panels'
    },
    {
      category: 'SOF Panels – PET',
      tagline: 'SUSTAINABLE INNOVATION',
      description: 'Panels and baffles cut from recycled polyester felt, for treatment that is light, durable, and easy to clean.',
      image: 'https://res.cloudinary.com/dwbfrbwul/image/upload/f_auto,q_auto/v1765090570/hillpoint/products/hillpoint/products/products-downloaded/sof-baffles.jpg',
      link: '/products#sof-panels-pet'
    },
    {
      category: 'Comfy Panels-Fabric',
      tagline: 'FABRIC SOPHISTICATION',
      description: 'Fabric-wrapped panels, tiles, and stretch systems rated up to NRC 0.90, in a wide range of finishes.',
      image: 'https://res.cloudinary.com/dwbfrbwul/image/upload/f_auto,q_auto/v1765090535/hillpoint/products/hillpoint/products/products-downloaded/comfy-panels.jpg',
      link: '/products#comfy-panels-fabric'
    },
    {
        category: 'Doors & Partitions',
        tagline: 'FLEXIBLE ARCHITECTURE',
        description: 'Lab-tested acoustic doors and operable partition walls that keep sound where it belongs.',
        image: 'https://res.cloudinary.com/dwbfrbwul/image/upload/f_auto,q_auto/v1765090585/hillpoint/products/hillpoint/products/products-downloaded/wooden-doors.jpg',
        link: '/products#doors-movable-partitions'
    },
    {
        category: 'Specialized Products',
        tagline: 'CUSTOM ENGINEERING',
        description: 'Louvers, isolation mounts, and noise-barrier membranes for the problems panels alone cannot solve.',
        image: 'https://res.cloudinary.com/dwbfrbwul/image/upload/f_auto,q_auto/v1765090551/hillpoint/products/hillpoint/products/products-downloaded/louver.jpg',
        link: '/products#specialized-products'
    },
  ];

  return (
    <section className="relative py-16 md:py-24 bg-silk-50 overflow-hidden">
      {/* Dithering Grain Overlay */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none z-20 mix-blend-multiply"
           style={{ filter: 'url(#grain)' }}></div>

      <div className="max-w-[1600px] mx-auto px-5 md:px-8 relative z-10">
        {/* Section Header - TIGHTER SPACING */}
        <div className="mb-12 md:mb-16 relative text-center">
            <span className="text-gold-deep font-serif italic text-xl md:text-2xl tracking-wider block mb-4">The Acousstop Range</span>

            <h2 className="font-serif font-bold text-hillpoint leading-tight tracking-tighter mb-8">
                 {/* Responsive single line text using clamp */}
                 <span className="block text-[clamp(2.5rem,5vw,6rem)]">
                    Absorption by <span className="italic text-gold-deep">Design</span>
                 </span>
            </h2>

            <div className="flex justify-center">
                <Link to="/products" className="group flex items-center gap-3 text-hillpoint hover:text-gold transition-colors duration-300">
                    <span className="text-sm md:text-base font-bold uppercase tracking-widest">View Catalog</span>
                    <div className="w-10 h-10 rounded-full border border-current flex items-center justify-center group-hover:bg-hillpoint group-hover:text-white transition-all duration-300">
                        <ArrowUpRight className="w-5 h-5" />
                    </div>
                </Link>
            </div>
            
            {/* Decorative Separator */}
            <div className="w-16 h-px bg-hillpoint/20 mx-auto mt-8"></div>
        </div>

        {/* Horizontal Scroll / Grid Hybrid for Maximalist Feel */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
            {/* First Major Item - Spans 8 columns */}
            <ProductCard product={products[0]} className="md:col-span-8 h-[400px] md:h-[600px]" priority />
            
            {/* Second Item - Spans 4 columns */}
            <ProductCard product={products[1]} className="md:col-span-4 h-[400px] md:h-[600px]" />
            
            {/* Third Item - Spans 4 columns */}
            <ProductCard product={products[2]} className="md:col-span-4 h-[350px] md:h-[500px]" />
            
             {/* Fourth Item - Spans 4 columns */}
             <ProductCard product={products[3]} className="md:col-span-4 h-[350px] md:h-[500px]" />

             {/* Fifth Item - Spans 4 columns */}
             <ProductCard product={products[4]} className="md:col-span-4 h-[350px] md:h-[500px]" />
        </div>
      </div>
    </section>
  );
};

const ProductCard = ({ product, className, priority = false }) => {
    return (
        <Link to={product.link} className={`group relative block overflow-hidden bg-white border border-stone-200 ${className}`}>
            {/* Image - Always Colored with Subtle Zoom */}
            <div className="absolute inset-0 overflow-hidden">
                {/* Gradient Overlay - Fades on Hover */}
                <div className="absolute inset-0 bg-gradient-to-t from-hillpoint/50 via-hillpoint/20 to-transparent opacity-60 group-hover:opacity-0 z-10 transition-opacity duration-700"></div>
                <img
                    src={productImage(product.image, 'wide')}
                    alt={product.category}
                    loading={priority ? 'eager' : 'lazy'}
                    width={1400}
                    height={933}
                    className="w-full h-full object-cover transform transition-all [transition-duration:1200ms] ease-out scale-105 group-hover:scale-110"
                />
            </div>

            {/* Floating Content Card - Enhanced Animation */}
            <div className="absolute bottom-0 left-0 w-full p-4 md:p-8 z-20">
                <div className="bg-white/95 backdrop-blur-md border border-white/40 p-4 md:p-6 transform transition-all duration-500 ease-out translate-y-2 group-hover:translate-y-0 group-hover:shadow-2xl shadow-lg">
                    <div className="flex justify-between items-start mb-2 md:mb-4">
                        <span className="text-gold-deep text-xs font-bold tracking-[0.2em] uppercase group-hover:text-hillpoint transition-colors duration-300">{product.tagline}</span>
                        <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-hillpoint/0 group-hover:bg-hillpoint flex items-center justify-center transition-all duration-300">
                            <ArrowRight className="w-4 h-4 md:w-5 md:h-5 text-hillpoint group-hover:text-white -rotate-45 group-hover:rotate-0 transition-all duration-300" />
                        </div>
                    </div>
                    <h3 className={`font-serif font-bold text-hillpoint mb-2 leading-tight ${priority ? 'text-2xl md:text-5xl' : 'text-xl md:text-3xl'}`}>
                        {product.category}
                    </h3>
                    <div className="max-h-0 overflow-hidden group-hover:max-h-32 transition-all duration-500 ease-out hidden md:block">
                        <p className="text-stone-600 pt-4 border-t border-stone-200 mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100">{product.description}</p>
                    </div>
                </div>
            </div>
        </Link>
    )
}

export default Industries;
